import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiPathCandidates, rawFetcher, secureFetcher } from "./queryHelpers";
import { useAppSelector } from "../State/Hooks/reduxHooks";
import { selectClientValue } from "../State/Slices/SelectedClientSlice";
import {
  ICSVUploadRequest,
  IImportChange,
  IImportConflict,
  IImportConflictPagination,
  IImportError,
  IImportStatus,
  IUserResolveCsv,
} from "../Typings/Imports";
import { useAuthToken } from "../State/Hooks/authHooks";
import { useCurrentUser } from "./UserQueries";
import { ImportStatus } from "../Enums/ImportStatus";
import { useImportStatusStore } from "../State/Stores/ImportStatusStore";
import { useShallow } from "zustand/react/shallow";
import { IPaginationResponse } from "../Typings/Campaigns";

export function useAllImportsForClient(groupId?: string) {
  const selectedClient = useAppSelector(selectClientValue);
  const token = useAuthToken();
  const cacheKey = groupId
    ? ["allimports", selectedClient.clientId, groupId]
    : ["allimports", selectedClient.clientId];
  const path = groupId
    ? `/candidates/imports/${selectedClient.clientId}/group/${groupId}`
    : `/candidates/imports/${selectedClient.clientId}`;
  return useQuery<IImportStatus[]>(
    cacheKey,
    async () => {
      var imports = (await secureFetcher(path, token)) as IImportStatus[];
      return imports.map(mapTotalSuccesses);
    },
    {
      enabled: !!token,
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
  );
}

export function useImportStatus(statusId: string) {
  const token = useAuthToken();
  return useQuery(
    ["import", statusId],
    async () => {
      const status = (await secureFetcher(`/candidates/import/${statusId}`, token)) as IImportStatus[];
      if (!status.length) {
        return undefined;
      }
      return mapTotalSuccesses(status[0]); // Should return a single status within the array matching the statusId
    },
    {
      enabled: !!token && !!statusId,
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchIntervalInBackground: true,
      refetchOnWindowFocus: false,
      refetchInterval: (data) => {
        if (data?.status === ImportStatus.Created || data?.status === ImportStatus.InProgress) {
          return 1000; // Poll every 1 second when Created or InProgress
        }
        return false;
      },
    },
  );
}

export const useImportChanges = (statusId: string, filterQueryString: string) => {
  const { data: importStatus } = useImportStatus(statusId);
  const token = useAuthToken();
  const queryString = `${filterQueryString ? filterQueryString + "&" : "?"}logType=changes`;
  const fetcherPath = `/candidates/import/${statusId}/logs${queryString}`;
  return useQuery<IPaginationResponse<IImportChange>>(
    ["importChanges", statusId, queryString],
    () => secureFetcher(fetcherPath, token),
    {
      enabled: !!token && !!statusId,
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchIntervalInBackground: true,
      refetchOnWindowFocus: false,
      refetchInterval: () => {
        if (
          importStatus?.status === ImportStatus.Created ||
          importStatus?.status === ImportStatus.InProgress
        ) {
          return 1000; // Poll every 1 second when Created or InProgress
        }
        return false;
      },
    },
  );
};

export const useImportConflicts = (statusId: string, filterQueryString: string) => {
  const { data: importStatus } = useImportStatus(statusId);
  const token = useAuthToken();
  const queryString = `${filterQueryString ? filterQueryString + "&" : "?"}logType=conflicts`;
  const fetcherPath = `/candidates/import/${statusId}/logs${queryString}`;
  return useQuery<IImportConflictPagination>(
    ["importConflicts", statusId, queryString],
    () => secureFetcher(fetcherPath, token),
    {
      enabled: !!token && !!statusId,
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchIntervalInBackground: true,
      refetchOnWindowFocus: false,
      refetchInterval: () => {
        if (
          importStatus?.status === ImportStatus.Created ||
          importStatus?.status === ImportStatus.InProgress
        ) {
          return 1000; // Poll every 1 second when Created or InProgress
        }
        return false;
      },
    },
  );
};

export const useImportErrors = (statusId: string, filterQueryString: string) => {
  const { data: importStatus } = useImportStatus(statusId);
  const token = useAuthToken();
  const queryString = `${filterQueryString ? filterQueryString + "&" : "?"}logType=errors`;
  const fetcherPath = `/candidates/import/${statusId}/logs${queryString}`;
  return useQuery<IPaginationResponse<IImportError>>(
    ["importErrors", statusId, queryString],
    () => secureFetcher(fetcherPath, token),
    {
      enabled: !!token && !!statusId,
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchIntervalInBackground: true,
      refetchOnWindowFocus: false,
      refetchInterval: () => {
        if (
          importStatus?.status === ImportStatus.Created ||
          importStatus?.status === ImportStatus.InProgress
        ) {
          return 1000; // Poll every 1 second when Created or InProgress
        }
        return false;
      },
    },
  );
};

export function useResolveConflict() {
  const token = useAuthToken();
  const user = useCurrentUser();
  const queryClient = useQueryClient();
  return useMutation(
    (resolve: IUserResolveCsv) =>
      secureFetcher(`/candidates/imports/resolve`, token, {
        method: "PUT",
        body: JSON.stringify({ ...resolve, userId: user?.id }),
        headers: {
          "Content-Type": "application/json",
        },
      }),
    {
      onSuccess: (_, resolve) => {
        queryClient.invalidateQueries({ queryKey: ["import", resolve.importStatusId] });
        return queryClient.refetchQueries({ queryKey: ["importConflicts", resolve.importStatusId] });
      },
    },
  );
}

/**
 * Triggers the import of candidates from a CSV file
 *
 * @returns UseMutationResult<boolean, unknown, FormData, unknown>
 */
export const useImportCandidateCSVMutation = () => {
  const queryClient = useQueryClient();
  const { clientId } = useAppSelector(selectClientValue);
  const { id } = useCurrentUser();
  const { setUploadError, setStatusId, setUploadSuccess } = useImportStatusStore(
    useShallow((state) => ({
      setStatusId: state.setStatusId,
      setUploadError: state.setUploadError,
      setUploadSuccess: state.setUploadSuccess,
    })),
  );

  const token = useAuthToken();

  return useMutation(
    (requestDto: ICSVUploadRequest) =>
      rawFetcher(
        apiPathCandidates +
          "import/" +
          clientId +
          (requestDto.groupId ? `/group/${requestDto.groupId}` : "") +
          `?userId=${id}`,
        {
          method: "POST",
          body: requestDto.file,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      ) as Promise<Response>,
    {
      onError: (error) => {
        if (error instanceof Error) {
          console.error(error);
          setUploadError(true, "There was an error uploading the file. Please try again."); // Network error or unhandled server error
        } else if (typeof error === "string") {
          setUploadError(true, error); // CSV validation error or handled server error
        }
        setStatusId(undefined);
      },
      onSuccess: async (response, request) => {
        const body = await response.json();
        if (body.errorMessage) {
          setUploadError(true, body.errorMessage); // CSV validation error or server error while processing
          setStatusId(undefined);
        } else if (body.importStatusId && body.importStatusId !== "00000000-0000-0000-0000-000000000000") {
          setUploadError(false, "");
          setStatusId(body.importStatusId);
          setUploadSuccess(true);

          if (request.groupId) {
            setTimeout(() => {
              queryClient.invalidateQueries(["groupProfile"]);
              queryClient.invalidateQueries(["groupMembers"]);
              queryClient.invalidateQueries(["groupMemberIds"]);
            }, 2000);
          }

          queryClient.invalidateQueries(["allimports", clientId]);
          queryClient.invalidateQueries(["candidates", clientId]);
        }

        queryClient.invalidateQueries(["allimports", clientId]);
      },
      mutationKey: ["importCandidateByCSV", clientId],
    },
  );
};

function mapTotalSuccesses(status: IImportStatus) {
  return {
    ...status,
    totalSuccesses: status.importCount + status.updateCount,
  } as IImportStatus;
}
